import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { forkJoin, Observable, ReplaySubject, Subject } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { ApiService } from '@services/api.service';
import { MemberService } from '@services/member.service';
import { IPermission } from '@core/interfaces/permission.interface';
import { SideAndTopBarService } from './side-and-top-bar.service';
import { Member } from '@app/models/member.model';
import { Roles } from '../constants/roles.constant';

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  private reportingPermissions: IPermission = {};
  private memberPermissions: IPermission = {};
  private groupPermissions: IPermission = {};
  private coursePermissions: IPermission = {};
  private iltPermissions: IPermission = {};
  private lpPermissions: IPermission = {};
  private companySettingPermissions: IPermission = {};
  private organizationPermissions: IPermission = {};
  private trainingSchedulePermissions: IPermission = {};
  private categoriesPermissions: IPermission = {};

  private currentMember: Member | null = null;
  private role = Roles;

  // Notifies subscribers when permission state has been (re)loaded. Late subscribers
  // still get the last emission so HeaderComponent (which subscribes in ngOnInit after
  // permissions may have already loaded during login) can recompute permission-derived
  // values in a fresh CD tick.
  private permissionsChangedSubject = new ReplaySubject<void>(1);
  public permissionsChanged$: Observable<void> =
    this.permissionsChangedSubject.asObservable();

  constructor(
    private api: ApiService,
    private memberService: MemberService,
    private sidebarService: SideAndTopBarService,
  ) {}

  public getReportingPermissions(): IPermission {
    return this.reportingPermissions;
  }

  public getCoursePermissions(): IPermission {
    return this.coursePermissions;
  }

  public getIltCoursePermissions(): IPermission {
    return this.iltPermissions;
  }

  public getLPPermissions(): IPermission {
    return this.lpPermissions;
  }

  public getCategoriesPermissions(): IPermission {
    return this.categoriesPermissions;
  }

  public getMemberPermissions(): IPermission {
    return this.memberPermissions;
  }

  // Re-fetch the current member's permissions and update the cache. Needed after
  // actions that change what the member may do (e.g. submitting a training for
  // publishing approval creates a request they can now review).
  public refreshMemberPermissions(): void {
    this.getPermission('Member')
      .pipe(take(1))
      .subscribe(permission => {
        this.memberPermissions = permission;
        this.permissionsChangedSubject.next();
      });
  }

  public getGroupPermissions(): IPermission {
    return this.groupPermissions;
  }

  public getCompanySettingPermissions(): IPermission {
    return this.companySettingPermissions;
  }

  public getOrganizationPermissions(): IPermission {
    return this.organizationPermissions;
  }

  public getTrainingSchedulePermissions(): IPermission {
    return this.trainingSchedulePermissions;
  }

  public setGeneralPermissions(): void {
    forkJoin({
      reporting: this.getPermission('CourseTalentReport').pipe(take(1)),
      schedule: this.getPermission('Schedule').pipe(take(1)),
      course: this.getPermission('Course').pipe(take(1)),
      ilt: this.getPermission('IltCourse').pipe(take(1)),
      lp: this.getPermission('LearningPath').pipe(take(1)),
      categories: this.getPermission('CourseCategory').pipe(take(1)),
      member: this.getPermission('Member').pipe(take(1)),
      group: this.getPermission('Group').pipe(take(1)),
      companySetting: this.getPermission('CompanySetting').pipe(take(1)),
      organisation: this.getPermission('OrganisationLevel').pipe(take(1)),
    })
      .pipe(take(1))
      .subscribe(perms => {
        this.reportingPermissions = perms.reporting;
        this.trainingSchedulePermissions = perms.schedule;
        this.coursePermissions = perms.course;
        this.iltPermissions = perms.ilt;
        this.lpPermissions = perms.lp;
        this.categoriesPermissions = perms.categories;
        this.memberPermissions = perms.member;
        this.groupPermissions = perms.group;
        this.companySettingPermissions = perms.companySetting;
        this.organizationPermissions = perms.organisation;
        if (
          this.currentMember &&
          this.currentMember.role === this.role.LEARNER
        ) {
          this.sidebarService.setValue(
            'hideMenuButton',
            perms.organisation.people_management,
          );
        }
        this.permissionsChangedSubject.next();
      });
  }

  public getPermission(
    resourceName: string,
    resourceId?: string,
  ): Observable<IPermission> {
    const subject = new Subject<IPermission>();

    this.memberService
      .current()
      .pipe(take(1))
      .subscribe(member => {
        if (!member) {
          subject.next({});
          return;
        }
        this.currentMember = member;
        let url = `/api/v1/permission?&resource_class=${resourceName}`;
        if (resourceId) {
          url += `&resource_id=${resourceId}`;
        }

        this.api
          .get(environment.backendBaseUrl, url)
          .pipe(
            take(1),
            catchError(() => {
              subject.next({});
              return [];
            }),
          )
          .subscribe(response => {
            subject.next(response.body);
          });
      });
    return subject.asObservable();
  }
}
